/**
 * CR096: Policy Audit Log - Unified audit for attribute visibility + publish rules
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, ArrowRight, User } from "lucide-react";
import { format } from "date-fns";

interface AuditEntry {
  id: number;
  policy_scope: string;
  policy_key: string;
  changed_by: string | null;
  changed_at: string;
  before_json: Record<string, unknown>;
  after_json: Record<string, unknown>;
}

interface PolicyAuditLogProps {
  entries: AuditEntry[];
  loading: boolean;
}

export function PolicyAuditLog({ entries, loading }: PolicyAuditLogProps) {
  const formatChange = (before: Record<string, unknown>, after: Record<string, unknown>, scope: string) => {
    const changes: string[] = [];
    
    if (scope === "ATTRIBUTE_VISIBILITY") {
      if (before.show_in_staging !== after.show_in_staging) {
        changes.push(`Staging: ${after.show_in_staging ? "ON" : "OFF"}`);
      }
      if (before.show_in_admin_prod !== after.show_in_admin_prod) {
        changes.push(`Admin: ${after.show_in_admin_prod ? "ON" : "OFF"}`);
      }
      if (before.show_in_public !== after.show_in_public) {
        changes.push(`Public: ${after.show_in_public ? "ON" : "OFF"}`);
      }
    } else if (scope === "PUBLISH_RULE") {
      if (Object.keys(before).length === 0) {
        changes.push("Created rule");
      } else if (before.is_enabled !== after.is_enabled) {
        changes.push(`Enabled: ${after.is_enabled ? "ON" : "OFF"}`);
      }
      if (before.severity !== after.severity) {
        changes.push(`Severity: ${after.severity}`);
      }
      if (before.rule_type !== after.rule_type) {
        changes.push(`Type: ${after.rule_type}`);
      }
    }
    
    return changes.length > 0 ? changes.join(", ") : "Updated";
  };

  const getScopeLabel = (scope: string) => {
    switch (scope) {
      case "ATTRIBUTE_VISIBILITY":
        return "Attribute";
      case "PUBLISH_RULE":
        return "Publish Rule";
      default:
        return scope;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Policy Audit Log
        </CardTitle>
        <CardDescription>
          Recent changes to attribute visibility and publish rules (last 25 entries)
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            No audit entries yet
          </p>
        ) : (
          <ScrollArea className="h-[300px]">
            <div className="space-y-2">
              {entries.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30"
                >
                  <Badge
                    variant={entry.policy_scope === "PUBLISH_RULE" ? "secondary" : "outline"}
                  >
                    {getScopeLabel(entry.policy_scope)}
                  </Badge>
                  <Badge variant="outline" className="font-mono text-xs">
                    {entry.policy_key}
                  </Badge>
                  <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm flex-1">
                    {formatChange(entry.before_json, entry.after_json, entry.policy_scope)}
                  </span>
                  <div className="text-right text-xs text-muted-foreground flex-shrink-0">
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      <span className="truncate max-w-[100px]">
                        {entry.changed_by || "Unknown"}
                      </span>
                    </div>
                    <span>
                      {format(new Date(entry.changed_at), "MMM d, HH:mm")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
